import React from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { Paper, Typography, Box, Grid } from '@mui/material';

interface DailyUsageData {
    Date: string;
    Requests: number;
    PromptTokens: number;
    CompletionTokens: number;
    TotalTokens: number;
    CostUsd?: number;
}

interface ModelUsageData {
    ModelDeployment: string;
    Requests: number;
    TotalTokens: number;
}

interface AiUsageChartProps {
    dailyData: DailyUsageData[];
    modelData: ModelUsageData[];
}

const AiUsageChart: React.FC<AiUsageChartProps> = ({ dailyData = [], modelData = [] }) => {
    // Sort daily data by date to ensure chronological order
    const sortedDaily = [...dailyData].sort((a, b) => new Date(a.Date).getTime() - new Date(b.Date).getTime());

    const dates = sortedDaily.map(d => {
        try {
            const dateObj = new Date(d.Date);
            return dateObj.toLocaleDateString(undefined, { month: 'short', day: 'numeric', timeZone: 'UTC' });
        } catch {
            return d.Date;
        }
    });
    const requests = sortedDaily.map(d => d.Requests);
    const tokens = sortedDaily.map(d => d.TotalTokens);

    // Calculate daily costs and cumulative cost progression
    let cumulativeSum = 0;
    const costs = sortedDaily.map(d => d.CostUsd ?? 0);
    const cumulativeCosts = sortedDaily.map(d => {
        cumulativeSum += d.CostUsd ?? 0;
        return cumulativeSum;
    });

    // Options for the main Usage Trends chart (Requests and Tokens)
    const trendOptions: Highcharts.Options = {
        chart: {
            type: 'areaspline',
            backgroundColor: 'transparent',
            style: {
                fontFamily: 'Inter, Roboto, Helvetica, Arial, sans-serif'
            },
            height: 350,
        },
        title: {
            text: undefined
        },
        credits: {
            enabled: false
        },
        xAxis: {
            categories: dates,
            gridLineWidth: 0,
            lineColor: 'rgba(0, 0, 0, 0.08)',
            tickColor: 'rgba(0, 0, 0, 0.08)',
            labels: {
                style: {
                    color: '#666666',
                    fontSize: '11px'
                }
            }
        },
        yAxis: [
            { // Primary yAxis (Requests)
                title: {
                    text: 'Requests',
                    style: {
                        color: '#3f51b5',
                        fontWeight: '600'
                    }
                },
                gridLineColor: 'rgba(0, 0, 0, 0.05)',
                labels: {
                    style: {
                        color: '#3f51b5'
                    }
                },
                min: 0,
            },
            { // Secondary yAxis (Tokens)
                title: {
                    text: 'Tokens Consumed',
                    style: {
                        color: '#e91e63',
                        fontWeight: '600'
                    }
                },
                gridLineColor: 'rgba(0, 0, 0, 0.05)',
                labels: {
                    style: {
                        color: '#e91e63'
                    }
                },
                opposite: true,
                min: 0,
            }
        ],
        tooltip: {
            shared: true,
            useHTML: true,
            headerFormat: '<span style="font-size: 11px; color: #666; font-weight: bold">{point.key}</span><br/>',
            pointFormat: '<span style="color:{point.color}">\u25CF</span> {series.name}: <b>{point.y:,.0f}</b><br/>',
            backgroundColor: 'rgba(255, 255, 255, 0.96)',
            borderRadius: 8,
            borderWidth: 1,
            borderColor: 'rgba(0, 0, 0, 0.08)',
            shadow: true,
        },
        legend: {
            align: 'center',
            verticalAlign: 'bottom',
            borderWidth: 0,
            itemStyle: {
                color: '#333333',
                fontSize: '12px',
                fontWeight: '500'
            }
        },
        plotOptions: {
            areaspline: {
                fillColor: {
                    linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
                    stops: [
                        [0, 'rgba(233, 30, 99, 0.15)'],
                        [1, 'rgba(233, 30, 99, 0.0)']
                    ]
                },
                marker: {
                    radius: 4,
                    states: {
                        hover: {
                            radius: 6
                        }
                    }
                },
                lineWidth: 2,
                states: {
                    hover: {
                        lineWidth: 3
                    }
                },
                threshold: null
            },
            column: {
                borderRadius: 4,
                maxPointWidth: 30
            }
        },
        series: [
            {
                type: 'column',
                name: 'Requests',
                data: requests,
                yAxis: 0,
                color: '#3f51b5',
            },
            {
                type: 'areaspline',
                name: 'Tokens',
                data: tokens,
                yAxis: 1,
                color: '#e91e63',
            }
        ]
    };

    // Options for the new Cost Trends chart (Daily Cost and Cumulative Cost)
    const costOptions: Highcharts.Options = {
        chart: {
            type: 'areaspline',
            backgroundColor: 'transparent',
            style: {
                fontFamily: 'Inter, Roboto, Helvetica, Arial, sans-serif'
            },
            height: 350,
        },
        title: {
            text: undefined
        },
        credits: {
            enabled: false
        },
        xAxis: {
            categories: dates,
            gridLineWidth: 0,
            lineColor: 'rgba(0, 0, 0, 0.08)',
            tickColor: 'rgba(0, 0, 0, 0.08)',
            labels: {
                style: {
                    color: '#666666',
                    fontSize: '11px'
                }
            }
        },
        yAxis: [
            { // Primary yAxis (Daily Cost)
                title: {
                    text: 'Daily Cost (USD)',
                    style: {
                        color: '#009688',
                        fontWeight: '600'
                    }
                },
                gridLineColor: 'rgba(0, 0, 0, 0.05)',
                labels: {
                    format: '${value:.2f}',
                    style: {
                        color: '#009688'
                    }
                },
                min: 0,
            },
            { // Secondary yAxis (Cumulative Cost)
                title: {
                    text: 'Cumulative Cost (USD)',
                    style: {
                        color: '#ff9800',
                        fontWeight: '600'
                    }
                },
                gridLineColor: 'rgba(0, 0, 0, 0.05)',
                labels: {
                    format: '${value:.2f}',
                    style: {
                        color: '#ff9800'
                    }
                },
                opposite: true,
                min: 0,
            }
        ],
        tooltip: {
            shared: true,
            useHTML: true,
            backgroundColor: 'rgba(255, 255, 255, 0.96)',
            borderRadius: 8,
            borderWidth: 1,
            borderColor: 'rgba(0, 0, 0, 0.08)',
            shadow: true,
            formatter: function (this: any) {
                let s = `<span style="font-size: 11px; color: #666; font-weight: bold">${this.x}</span><br/>`;
                this.points.forEach((point: any) => {
                    s += `<span style="color:${point.color}">\u25CF</span> ${point.series.name}: <b>$${point.y.toFixed(4)}</b><br/>`;
                });
                return s;
            }
        },
        legend: {
            align: 'center',
            verticalAlign: 'bottom',
            borderWidth: 0,
            itemStyle: {
                color: '#333333',
                fontSize: '12px',
                fontWeight: '500'
            }
        },
        plotOptions: {
            areaspline: {
                fillColor: {
                    linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
                    stops: [
                        [0, 'rgba(255, 152, 0, 0.12)'],
                        [1, 'rgba(255, 152, 0, 0.0)']
                    ]
                },
                marker: {
                    radius: 4,
                    states: {
                        hover: {
                            radius: 6
                        }
                    }
                },
                lineWidth: 2,
                states: {
                    hover: {
                        lineWidth: 3
                    }
                },
                threshold: null
            }
        },
        series: [
            {
                type: 'spline',
                name: 'Daily Cost',
                data: costs,
                yAxis: 0,
                color: '#009688',
            },
            {
                type: 'areaspline',
                name: 'Cumulative Cost',
                data: cumulativeCosts,
                yAxis: 1,
                color: '#ff9800',
            }
        ]
    };

    // Options for the model breakdown chart
    const modelCategories = modelData.map(m => m.ModelDeployment);
    const modelTokens = modelData.map(m => m.TotalTokens);
    const modelRequests = modelData.map(m => m.Requests);

    const modelOptions: Highcharts.Options = {
        chart: {
            type: 'bar',
            backgroundColor: 'transparent',
            style: {
                fontFamily: 'Inter, Roboto, Helvetica, Arial, sans-serif'
            },
            height: 350,
        },
        title: {
            text: undefined
        },
        credits: {
            enabled: false
        },
        xAxis: {
            categories: modelCategories,
            gridLineWidth: 0,
            lineColor: 'rgba(0, 0, 0, 0.08)',
            labels: {
                style: {
                    color: '#333333',
                    fontSize: '11px',
                    fontWeight: '600'
                }
            }
        },
        yAxis: [
            {
                title: {
                    text: 'Tokens Consumed',
                    style: {
                        color: '#009688',
                        fontWeight: '600'
                    }
                },
                gridLineColor: 'rgba(0, 0, 0, 0.05)',
                labels: {
                    style: {
                        color: '#009688'
                    }
                },
                min: 0,
            },
            {
                title: {
                    text: 'Requests',
                    style: {
                        color: '#00bcd4',
                        fontWeight: '600'
                    }
                },
                gridLineColor: 'rgba(0, 0, 0, 0.05)',
                labels: {
                    style: {
                        color: '#00bcd4'
                    }
                },
                opposite: true,
                min: 0,
            }
        ],
        tooltip: {
            shared: true,
            useHTML: true,
            backgroundColor: 'rgba(255, 255, 255, 0.96)',
            borderRadius: 8,
            borderWidth: 1,
            borderColor: 'rgba(0, 0, 0, 0.08)',
            shadow: true,
        },
        legend: {
            align: 'center',
            verticalAlign: 'bottom',
            borderWidth: 0,
            itemStyle: {
                color: '#333333',
                fontSize: '12px',
                fontWeight: '500'
            }
        },
        plotOptions: {
            bar: {
                borderRadius: 4,
                dataLabels: {
                    enabled: false
                }
            }
        },
        series: [
            {
                type: 'bar',
                name: 'Tokens',
                data: modelTokens,
                yAxis: 0,
                color: '#009688',
            },
            {
                type: 'bar',
                name: 'Requests',
                data: modelRequests,
                yAxis: 1,
                color: '#00bcd4',
            }
        ]
    };

    return (
        <Grid container spacing={3}>
            {/* Top row: Usage Trends & Model Breakdown */}
            <Grid size={{ xs: 12, lg: 8 }}>
                <Paper
                    elevation={3}
                    sx={{
                        p: 3,
                        borderRadius: 3,
                        background: 'rgba(255, 255, 255, 0.9)',
                        backdropFilter: 'blur(8px)',
                        border: '1px solid rgba(0, 0, 0, 0.08)',
                        height: '100%'
                    }}
                >
                    <Box mb={2}>
                        <Typography variant="h6" fontWeight="600" color="textPrimary">
                            Usage Trends
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                            Daily requests volume and token consumption trends over time
                        </Typography>
                    </Box>
                    <Box sx={{ width: '100%', overflow: 'hidden' }}>
                        {sortedDaily.length === 0 ? (
                            <Box display="flex" justifyContent="center" alignItems="center" height={290}>
                                <Typography color="textSecondary">No trend data available for the selected period.</Typography>
                            </Box>
                        ) : (
                            <HighchartsReact highcharts={Highcharts} options={trendOptions} />
                        )}
                    </Box>
                </Paper>
            </Grid>
            
            <Grid size={{ xs: 12, lg: 4 }}>
                <Paper
                    elevation={3}
                    sx={{
                        p: 3,
                        borderRadius: 3,
                        background: 'rgba(255, 255, 255, 0.9)',
                        backdropFilter: 'blur(8px)',
                        border: '1px solid rgba(0, 0, 0, 0.08)',
                        height: '100%'
                    }}
                >
                    <Box mb={2}>
                        <Typography variant="h6" fontWeight="600" color="textPrimary">
                            Model Breakdown
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                            Usage volume and token distribution by model deployment
                        </Typography>
                    </Box>
                    <Box sx={{ width: '100%', overflow: 'hidden' }}>
                        {modelData.length === 0 ? (
                            <Box display="flex" justifyContent="center" alignItems="center" height={290}>
                                <Typography color="textSecondary">No model data available.</Typography>
                            </Box>
                        ) : (
                            <HighchartsReact highcharts={Highcharts} options={modelOptions} />
                        )}
                    </Box>
                </Paper>
            </Grid>

            {/* Bottom row: Cost Trends (Full Width) */}
            <Grid size={12}>
                <Paper
                    elevation={3}
                    sx={{
                        p: 3,
                        borderRadius: 3,
                        background: 'rgba(255, 255, 255, 0.9)',
                        backdropFilter: 'blur(8px)',
                        border: '1px solid rgba(0, 0, 0, 0.08)',
                        height: '100%'
                    }}
                >
                    <Box mb={2}>
                        <Typography variant="h6" fontWeight="600" color="textPrimary">
                            Cost Trends & Growth
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                            Daily cost tracking alongside cumulative monthly cost growth (EOM)
                        </Typography>
                    </Box>
                    <Box sx={{ width: '100%', overflow: 'hidden' }}>
                        {sortedDaily.length === 0 ? (
                            <Box display="flex" justifyContent="center" alignItems="center" height={290}>
                                <Typography color="textSecondary">No cost trend data available for the selected period.</Typography>
                            </Box>
                        ) : (
                            <HighchartsReact highcharts={Highcharts} options={costOptions} />
                        )}
                    </Box>
                </Paper>
            </Grid>
        </Grid>
    );
};

export default AiUsageChart;
